import { useState } from 'react';
import { Check, Moon, X } from 'lucide-react';
import type { SandmanProposalRow } from '@command-room/shared';
import { useProposals, useDecideProposal } from '@/api/queries';
import { TableSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { relativeTime } from '@/lib/format';

const KIND_LABEL: Record<SandmanProposalRow['kind'], string> = {
  retire_skill: 'Retire skill',
  spec_amendment: 'Spec amendment',
  promote_knowledge: 'Promote knowledge',
};

function ProposalDetail({ proposal }: { proposal: SandmanProposalRow }) {
  const payload = proposal.payload ?? {};
  if (proposal.kind === 'spec_amendment') {
    return (
      <div className="flex flex-col gap-1 text-sm">
        <p className="text-muted-foreground">
          Section: <span className="font-mono text-foreground">{String(payload.section ?? '—')}</span>
        </p>
        <p className="whitespace-pre-wrap">{String(payload.suggested_text ?? '')}</p>
      </div>
    );
  }
  if (proposal.kind === 'promote_knowledge') {
    const agentNames = Array.isArray(payload.agent_names) ? (payload.agent_names as string[]) : [];
    return (
      <div className="flex flex-col gap-1 text-sm">
        <p className="whitespace-pre-wrap">{String(payload.fact_text ?? '')}</p>
        <p className="text-muted-foreground">
          Independently learned by: {agentNames.join(', ') || '—'}
        </p>
      </div>
    );
  }
  // retire_skill
  return (
    <p className="text-sm">
      Skill <span className="font-mono">{proposal.target_ref}</span> — not used in any request for 30+ days.
    </p>
  );
}

function DecideDialog({
  proposal,
  decision,
  onCancel,
  onConfirm,
  isPending,
}: {
  proposal: SandmanProposalRow | null;
  decision: 'approved' | 'rejected';
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const verb = decision === 'approved' ? 'Approve' : 'Reject';
  const description =
    decision === 'approved'
      ? proposal?.kind === 'retire_skill'
        ? 'Marks the skill retired — it stops loading into the agent immediately.'
        : proposal?.kind === 'spec_amendment'
          ? 'Writes this section into the agent spec and flags it for reload.'
          : 'Promotes this fact to global scope and archives the per-agent copies.'
      : 'Dismisses the proposal. It will not be re-proposed unless Sandman finds new evidence.';

  return (
    <Dialog open={!!proposal} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent title={`${verb} proposal`} description={description}>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            variant={decision === 'approved' ? 'default' : 'destructive'}
            disabled={isPending}
            onClick={onConfirm}
          >
            {isPending ? 'Saving…' : verb}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProposalCard({ proposal }: { proposal: SandmanProposalRow }) {
  const decide = useDecideProposal();
  const [confirm, setConfirm] = useState<'approved' | 'rejected' | null>(null);

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{KIND_LABEL[proposal.kind]}</Badge>
            <span className="text-sm font-medium">{proposal.agent_name}</span>
            <span className="text-xs text-muted-foreground">{relativeTime(proposal.created_at)}</span>
          </div>
          {proposal.rationale && (
            <p className="text-xs text-muted-foreground">{proposal.rationale}</p>
          )}
        </div>
        {proposal.status !== 'pending' && (
          <Badge variant={proposal.status === 'approved' ? 'success' : 'muted'}>{proposal.status}</Badge>
        )}
      </div>

      <ProposalDetail proposal={proposal} />

      {proposal.status === 'pending' && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirm('rejected')}>
            <X className="h-3.5 w-3.5" />
            Reject
          </Button>
          <Button size="sm" onClick={() => setConfirm('approved')}>
            <Check className="h-3.5 w-3.5" />
            Approve
          </Button>
        </div>
      )}

      <DecideDialog
        proposal={confirm ? proposal : null}
        decision={confirm ?? 'approved'}
        onCancel={() => setConfirm(null)}
        isPending={decide.isPending}
        onConfirm={() => {
          if (!confirm) return;
          decide.mutate({ id: proposal.id, decision: confirm }, { onSuccess: () => setConfirm(null) });
        }}
      />
    </div>
  );
}

export function ProposalsPage() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const { data: proposals, isLoading } = useProposals(statusFilter);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Moon className="h-5 w-5 text-muted-foreground" />
          Sandman proposals
        </h1>
        <p className="text-sm text-muted-foreground">
          Skill retirements, spec amendments, and knowledge promotions Sandman surfaced overnight —
          nothing here is applied until you approve it.
        </p>
      </header>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={statusFilter} className="mt-4">
          {isLoading ? (
            <TableSkeleton widths={['w-full']} rows={4} />
          ) : (proposals ?? []).length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Nothing here.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {(proposals ?? []).map((p) => (
                <ProposalCard key={p.id} proposal={p} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
